import { User } from '../types';

export const getLocalDateString = (date: Date): string => {
  // Use local timezone instead of UTC to prevent timezone issues
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const getLocalDateFromString = (dateString: string): Date => {
  return new Date(dateString + 'T00:00:00');
};

/**
 * Returns YYYY-MM-DD in the user's preferred timezone (not the device's local TZ).
 * Use this when querying date-bucketed data like Garmin activities so the user's
 * "today" matches their lived-in timezone, not whatever zone the browser is in.
 */
export const getDateStringInTimezone = (date: Date, timezone?: string): string => {
  if (!timezone) return getLocalDateString(date);
  // 'en-CA' locale formats dates as YYYY-MM-DD which matches our calendarDate format
  try {
    return date.toLocaleDateString('en-CA', { timeZone: timezone });
  } catch {
    // Invalid timezone string — fall back to device local
    return getLocalDateString(date);
  }
};

// Shared calorie and macro calculation utilities
/**
 * The calorie nudge a stated goal implies when no target weight has been set:
 * "lose weight" (onboarding `lose_weight`, profile "Weight Loss") takes 15% off
 * maintenance (at most 500 cal — about half a pound a week for most people),
 * "gain weight" adds 10% (at most 300). A concrete weight goal + timeframe
 * replaces this with the exact deficit or surplus it needs.
 */
export const goalCalorieAdjustment = (healthGoals: string[] | null | undefined, maintenanceCalories: number): number => {
  const goals = (healthGoals || []).map((g) => String(g || '').toLowerCase().replace(/_/g, ' '));
  const wantsLoss = goals.some((g) => g.includes('lose weight') || g.includes('weight loss') || g.includes('fat loss'));
  const wantsGain = goals.some((g) => g.includes('gain weight') || g.includes('weight gain') || g.includes('bulk'));
  if (wantsLoss && !wantsGain) return Math.min(500, Math.round(maintenanceCalories * 0.15));
  if (wantsGain && !wantsLoss) return -Math.min(300, Math.round(maintenanceCalories * 0.10));
  return 0;
};

export const calculateSuggestedCalories = (user: User | null): number | null => {
  if (
    !user ||
    user.weight == null ||
    user.height == null ||
    !user.gender ||
    !user.activityLevel ||
    !user.dateOfBirth
  ) {
    return null;
  }

  const age = Math.floor((Date.now() - new Date(user.dateOfBirth).getTime()) / (365.25 * 24 * 60 * 60 * 1000));
  
  const heightInCm = user.height; 
  const weightInKg = user.weight;

  let bmr: number;
  if (user.gender === 'male') {
    bmr = 10 * weightInKg + 6.25 * heightInCm - 5 * age + 5;
  } else {
    bmr = 10 * weightInKg + 6.25 * heightInCm - 5 * age - 161;
  }

  // Use consistent activity multipliers
  const activityMultipliers = {
    sedentary: 1.2,
    lightly_active: 1.375,
    moderately_active: 1.55,
    very_active: 1.725,
    extra_active: 1.9
  };

  const maintenanceCalories = bmr * (activityMultipliers[user.activityLevel] || 1.2);

  // Weight goal adjustment: the exact deficit/surplus when a target weight and
  // timeframe are set; otherwise the nudge the stated goal implies.
  let dailyCalorieAdjustment = goalCalorieAdjustment(user.healthGoals, maintenanceCalories);
  if (user.weightGoal != null && user.weightGoalTimeframe != null && user.weightGoalTimeframe > 0) {
    const weightChangeLbs = (weightInKg - user.weightGoal) * 2.20462;
    const weeklyWeightChange = weightChangeLbs / user.weightGoalTimeframe;
    const safeWeeklyWeightChange = Math.min(Math.abs(weeklyWeightChange), 2) * Math.sign(weeklyWeightChange);
    dailyCalorieAdjustment = (safeWeeklyWeightChange * 3500) / 7;
  }

  const suggestedCalories = Math.round(maintenanceCalories - dailyCalorieAdjustment);
  
  const minimumCalories = user.gender === 'male' ? 1500 : 1200;
  
  return Math.max(suggestedCalories, minimumCalories);
};

export const calculateMacroGoals = (suggestedCalories: number, user: User | null) => {
  if (!user || !user.weight) {
    return {
      protein: 50,
      carbs: 300,
      fat: 67,
      fiber: 25,
      saturatedFat: Math.round(suggestedCalories * 0.10 / 9),
      sodium: 2300,
    };
  }

  // Normalize health goals — DB stores "Muscle Building", "Low Carb" etc.
  const goals = (user.healthGoals || []).map(g => g.toLowerCase());
  const isBuildMuscle = goals.some(g => g.includes('muscle') || g.includes('strength training'));
  const isLowCarb = goals.some(g => g.includes('low carb'));

  // Protein per kg based on goal:
  // Muscle building/strength: 2.0g/kg (high end for hypertrophy)
  // Default (active/weight loss): 1.6g/kg (preserves muscle during deficit)
  let proteinPerKg = isBuildMuscle ? 2.0 : 1.6;

  // Age boost: under 25 (growth phase) gets +10% protein
  if (user.dateOfBirth) {
    const age = Math.floor((Date.now() - new Date(user.dateOfBirth).getTime()) / (365.25 * 24 * 60 * 60 * 1000));
    if (age < 25) proteinPerKg *= 1.1;
  }

  const proteinGoal = Math.round(user.weight * proteinPerKg);
  const proteinCalories = proteinGoal * 4;

  // Fat: 30% standard, 40% low carb (up from 25%/40%)
  const fatPct = isLowCarb ? 0.40 : 0.30;
  const fatCalories = suggestedCalories * fatPct;
  const fatGoal = Math.round(fatCalories / 9);

  // Carbs: remainder, capped at 400g to prevent bloat at high calorie levels
  const carbCalories = suggestedCalories - proteinCalories - fatCalories;
  const carbGoal = Math.min(400, Math.round(Math.max(0, carbCalories) / 4));

  // If carbs were capped, redistribute excess to fat
  const actualCarbCalories = carbGoal * 4;
  const excessCalories = carbCalories - actualCarbCalories;
  const finalFatGoal = excessCalories > 0 ? fatGoal + Math.round(excessCalories / 9) : fatGoal;

  // Fiber: USDA DRI — 38g/day men, 25g/day women. Or 14g per 1000 cal.
  // We use gender-based since it's simpler and well-established.
  const fiberGoal = user.gender === 'female' ? 25 : 38;

  // Saturated fat: AHA/USDA guideline is <10% of total calories.
  // At 9 cal/g: (suggestedCalories × 0.10) / 9
  const saturatedFatGoal = Math.round((suggestedCalories * 0.10) / 9);

  // Sodium: AHA recommends <1500mg for heart health goals; USDA default 2300mg
  const isHeartFocused = goals.some(g => g.includes('heart') || g.includes('blood pressure') || g.includes('hypertension') || g.includes('cardiovascular'));
  const sodiumGoal = isHeartFocused ? 1500 : 2300;

  return {
    protein: proteinGoal,
    carbs: carbGoal,
    fat: Math.max(0, finalFatGoal),
    fiber: fiberGoal,
    saturatedFat: saturatedFatGoal,
    sodium: sodiumGoal,
  };
};

export const applyMacroAdjustments = (baseMacros: { protein: number; carbs: number; fat: number }, user: User | null) => {
  if (!user || !user.customMacroPreferences?.enabled) {
    return baseMacros;
  }

  const { proteinAdjustment = 0, carbAdjustment = 0, fatAdjustment = 0 } = user.customMacroPreferences;
  
  const adjustedMacros = {
    protein: Math.round(baseMacros.protein * (1 + proteinAdjustment / 100)),
    carbs: Math.round(baseMacros.carbs * (1 + carbAdjustment / 100)),
    fat: Math.round(baseMacros.fat * (1 + fatAdjustment / 100))
  };

  // Recalculate total calories based on adjusted macros
  const adjustedCalories = (adjustedMacros.protein * 4) + (adjustedMacros.carbs * 4) + (adjustedMacros.fat * 9);
  
  return {
    ...adjustedMacros,
    calories: Math.round(adjustedCalories)
  };
}; 